import { getSocketToken } from './getSocketToken'

const MOVE_JOB_COMPLETED_EVENT = 'item_job_completed'
const DEFAULT_TIMEOUT_MS = 30_000

type SocketEnvelope = {
  channel?: string
  'event-type'?: string
}

const resolveSocketUrl = (token: string): string | null => {
  const explicit = process.env.REACT_APP_CLUSTER_FILES_WS_ENDPOINT
  if (explicit) return `${explicit}?token=${encodeURIComponent(token)}`

  // Fallback: derive the WS endpoint from the HTTPS service URL.
  const service = process.env.REACT_APP_CLUSTER_FILES_SERVICE
  if (!service) return null
  const wsBase = service.replace(/^https:\/\//, 'wss://').replace(/\/$/, '')
  return `${wsBase}/ws?token=${encodeURIComponent(token)}`
}

/**
 * One-shot WebSocket that resolves when the server emits `item_job_completed`
 * for `jobId`, and rejects on error, abnormal close, or timeout. Always
 * unsubscribes and closes before settling.
 */
export const watchMoveJob = async (
  jobId: string,
  options: { timeoutMs?: number } = {}
): Promise<void> => {
  const token = await getSocketToken()
  const url = resolveSocketUrl(token)
  if (!url) throw new Error('No cluster-files WS endpoint configured')

  return new Promise<void>((resolve, reject) => {
    const ws = new WebSocket(url)
    let settled = false

    const finish = (err?: Error) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'unsubscribe', channel: jobId }))
        }
      } catch {
        // ignore — the socket may already be torn down
      }
      try {
        ws.close()
      } catch {
        // ignore
      }
      if (err) reject(err)
      else resolve()
    }

    const timeoutId = window.setTimeout(
      () => finish(new Error('watchMoveJob: timed out')),
      options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    )

    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({ action: 'subscribe', channel: jobId }))
    })

    ws.addEventListener('message', event => {
      try {
        const msg = JSON.parse(event.data) as SocketEnvelope
        if (
          msg.channel === jobId &&
          msg['event-type'] === MOVE_JOB_COMPLETED_EVENT
        ) {
          finish()
        }
      } catch {
        // Ignore non-JSON or unrelated frames
      }
    })

    ws.addEventListener('error', () => {
      finish(new Error('watchMoveJob: socket error'))
    })

    ws.addEventListener('close', event => {
      // Treat a clean close as "stop waiting" rather than an error; only
      // surface abnormal closes.
      if (event.wasClean) {
        finish()
      } else {
        finish(new Error('watchMoveJob: socket closed before completion'))
      }
    })
  })
}
